import { cn } from "@/lib/utils";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import { Modal } from "@/src/components/Modal/Modal";
import AppendInput from "@/src/components/PhoneInput/AppendInput";
import { textVariants } from "@/src/components/Text/Text";
import PhoneInput from "@/src/components/PhoneInput/AppendInput";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@components/shad/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/shad/popover";
import { ChevronsUpDown } from "lucide-react";
import api, { getFlagEmoji } from "@/src/utils";
import { useCountries } from "@/src/hooks/useCountries";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { Check } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import apiRoutes from "@/src/apiRoutes";
import _ from "lodash";
import { useAppSelector } from "@/src/app/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  data: PaintProfile | undefined;
  open: boolean;
  setOpen: (val: boolean) => void;
}

const formState = z.object({
  otp: z.string().min(4, { message: "Verification code is required" }),
  phone: z.string().min(1, { message: "Phone number is required" }),
  country_code: z.string().refine(() => ({
    path: ["phone"],
  })),
});

const ChangePhone = ({ data, open, setOpen }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) =>
    t(`app.settings.securityCenter.verificationMethods.phone.modal.${key}`);
  const { user } = useAppSelector((state) => state.user);
  type formType = z.infer<typeof formState>;
  const {
    watch,
    setValue,
    register,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm<formType>({
    resolver: zodResolver(formState),
  });
  // const [modal, setModal] = useUrlState("modal");
  const { countries } = useCountries();

  const selectedCountry =
    countries?.info.find(
      (item) => item?.short_code_iso3 === watch("country_code")
    ) ?? countries?.info.find((item) => item?.name === user?.country);

  const change = async () => {
    const newVals = _.omit(getValues(), "country_code");
    const res = await api.post(apiRoutes.phoneChange, {
      ...newVals,
      phone: `${getValues().country_code}${getValues().phone}`,
      type: "change",
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: change,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        // setModal(undefined);
        setOpen(false);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const submit: SubmitHandler<formType> = () => {
    mutate();
  };

  const sendOtp = async () => {
    const res = await api.post(apiRoutes.phoneChange, {
      type: "request",
      phone: `${watch("country_code")}${watch("phone")}`,
    });
    return res.data;
  };

  const { mutate: mutateOtp, isLoading: otpLoad } = useMutation({
    mutationFn: sendOtp,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        // setOpen(false);s
      } else {
        toast.warning(data?.info);
      }
    },
  });

  useEffect(() => {
    if (countries && selectedCountry) {
      setValue("country_code", selectedCountry?.short_code_iso3 as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, countries]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={trans("title")}
      setter="state"
      footer={
        <Button loading={isLoading} onClick={handleSubmit(submit)}>
          {trans("form.submitButton")}
        </Button>
      }
    >
      <div className="grid gap-4">
        <InputComponent
          readOnly
          value={data?.phone}
          label={trans("form.currentPhone.label")}
          className="text-base font-medium"
        />
        {/* <InputComponent label="Enter New Phone Number" type="email" /> */}
        <PhoneInput
          {...register("phone")}
          type="number"
          label={trans("form.newPhone.label")}
          selectOption={
            <Popover>
              <div className=" grid ">
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn(
                      " flex justify-between text-black font-medium w-full items-center border-gray-200 bg-input-field rounded-md px-3 py-2.5 text-sm  shadow-none dark:bg-grey-500 dark:text-white whitespace-nowrap border-0"
                    )}
                  >
                    {selectedCountry ? (
                      <>
                        {getFlagEmoji(selectedCountry?.short_code as string)} +
                        {selectedCountry?.short_code_iso3}
                      </>
                    ) : (
                      <span>Eg. +233</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 " />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className={cn(
                    ` max-h-[--radix-popover-content-available-height] overflow-y-scroll p-0 min-w-[var(--radix-popover-trigger-width)]  z-[100] `
                  )}
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder={`Search country`} />
                    <CommandEmpty>Nothing found.</CommandEmpty>
                    <CommandGroup className="gap-2 grid">
                      {countries?.info.map((item, index) => (
                        <CommandItem
                          key={index}
                          onSelect={() => {
                            setValue("country_code", item?.short_code_iso3);
                          }}
                          value={item?.name + item?.short_code_iso3}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              watch("country_code") === item?.short_code_iso3
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />

                          <div className="flex items-center h-1">
                            <span className="text-lg mr-2">
                              {getFlagEmoji(item?.short_code)}
                            </span>
                            <span>
                              {item?.name} (+{item?.short_code_iso3})
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </div>
            </Popover>
          }
          autoComplete="off"
          errors={errors.phone?.message || errors.country_code?.message}
          {...register("phone", { required: true })}
        />
        <AppendInput
          {...register("otp")}
          errors={errors.otp?.message}
          label={trans("form.verificationCode.label")}
          right={
            <div className="py-2 pr-2">
              <span
                onClick={() => mutateOtp()}
                className={cn(
                  "whitespace-nowrap cursor-pointer ",
                  textVariants({ variant: "link", size: "sm" })
                )}
              >
                {otpLoad ? trans("form.sending") : trans("form.getCode")}
              </span>
            </div>
          }
        />
      </div>
    </Modal>
  );
};

export default ChangePhone;
